import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "shared/contexts/I18nContext";
import { useLoading } from "shared/contexts/Loading";
import { useSubscription } from "shared/contexts/Subscription";
import ContentHeaderSection from "../../shared/common/ContentHeaderSection";
import KebabMenu from "shared/common/KebabMenu";
import FeatureLimitDisplay from "shared/common/FeatureLimitDisplay";
import apiCall from "shared/services/ApiAdapter";
import masterApiCall from "shared/services/MasterDataApi";
import { getCurrentUserId } from "shared/utils/SessionDataUtils";
import { loadMathJaxScript } from "../assessment/loadMathJaxScript";
import { ProtectedVisibility } from "shared/security/ProtectedRoute";
import { ShowQuestions } from "modules/questions/ShowQuestions";

// =========================================================================================
// --- NEW COMPONENT: Quiz Mode Selection Modal (Grayscale Styling) ---
const QuizModeSelectionModal = ({ evaluation, onClose, onSelectMode, t }) => {
  if (!evaluation) return null;

  const handlePresentation = () => {
    // 'presentation' is the mode identifier
    onSelectMode(evaluation, "presentation");
  };

  const handleInteractive = () => {
    // 'interactive' is the mode identifier
    onSelectMode(evaluation, "interactive");
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-900 bg-opacity-75 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-2xl w-full max-w-md transform transition-all duration-300 scale-100">
        <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-gray-100 border-b pb-2 border-gray-300 dark:border-gray-600">
          Launch Quiz: {evaluation.name}
        </h3>
        <p className="mb-6 text-gray-700 dark:text-gray-300">
          Select the quiz mode for {evaluation.name}
        </p>

        <div className="space-y-4">
          <button
            onClick={handlePresentation}
            className="w-full flex items-center justify-center gap-2 
                    bg-gray-700 text-white 
                    py-3 px-4 rounded-lg font-semibold text-lg 
                    hover:bg-gray-900 
                    dark:bg-gray-600 dark:hover:bg-gray-700
                    transition-colors shadow-md"
          >
            <span className="text-xl">📺</span> Presentation Mode
          </button>
          <button
            onClick={handleInteractive}
            className="w-full flex items-center justify-center gap-2 
                    bg-gray-500 text-white 
                    py-3 px-4 rounded-lg font-semibold text-lg 
                    hover:bg-gray-600 
                    dark:bg-gray-500 dark:hover:bg-gray-600
                    transition-colors shadow-md"
          >
            <span className="text-xl">🧑‍💻</span> Interactive Mode
          </button>
        </div>

        <button
          onClick={onClose}
          className="mt-6 w-full text-gray-600 dark:text-gray-400 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};
// =========================================================================================


// --- Helper: Group and Sort by Course ---
const groupAndSortEvaluations = (allEvaluations) => {
  const grouped = allEvaluations.reduce((acc, evaluation) => {
    const courseId = evaluation.course?.id ?? evaluation.courseId;
    if (!courseId) return acc;

    if (!acc[courseId]) {
      acc[courseId] = [];
    }
    acc[courseId].push(evaluation);
    return acc;
  }, {});

  for (const courseId in grouped) {
    grouped[courseId].sort(
      (a, b) => new Date(b.createdDate) - new Date(a.createdDate)
    );
  }

  return grouped;
};

// --- Sub-Component: Evaluation Card ---
const EvaluationCard = ({
  evaluation,
  assignmentCount = 0,
  assignmentsLoaded = false,
  navigateTo,
  handleViewQuestions,
  handleAssignToBatch,
  handleLaunchQuizSelection,
  handleEditEvaluation,
  handleArchiveEvaluation,
  handleUnarchiveEvaluation,
  t,
  displayToast,
}) => {
  const isArchived =
    evaluation.isActive === false ||
    evaluation.isArchived === true ||
    evaluation.archived === true ||
    evaluation.status === "ARCHIVED" ||
    evaluation.status === "INACTIVE" ||
    evaluation.status === "DELETED";

  const expiryStr = evaluation.expiryDateTime || evaluation.expiryDate;
  const isExpired = Boolean(
    expiryStr &&
    !isNaN(new Date(expiryStr).getTime()) &&
    new Date(expiryStr) < new Date()
  );

  const hasZeroAssignments = assignmentsLoaded && assignmentCount === 0;

  return (
    <div
      onClick={() => navigateTo("viewAssignments", evaluation.id)}
      className={`relative flex flex-col justify-between items-start p-6 h-full
rounded-xl border shadow-sm hover:shadow-md hover:scale-[1.01]
transition-all duration-300 ease-out cursor-pointer
${
  isArchived
    ? "border-purple-300 dark:border-purple-800 bg-gradient-to-br from-purple-50/30 via-white to-gray-50 dark:from-purple-950/20 dark:via-gray-900 dark:to-gray-800"
    : isExpired
    ? "border-red-400 dark:border-red-500/80 ring-1 ring-red-400/40 dark:ring-red-500/30 bg-gradient-to-br from-red-50/40 via-white to-gray-50 dark:from-red-950/20 dark:via-gray-900 dark:to-gray-800"
    : hasZeroAssignments
    ? "border-amber-400 dark:border-amber-500/80 ring-1 ring-amber-400/40 dark:ring-amber-500/30 bg-gradient-to-br from-amber-50/40 via-white to-gray-50 dark:from-amber-950/20 dark:via-gray-900 dark:to-gray-800"
    : "border-gray-300 dark:border-gray-700 bg-gradient-to-br from-white to-gray-50 dark:from-gray-900 dark:to-gray-800"
}`}
    >
      {/* Top action buttons */}
      <div className="absolute top-4 right-4 flex items-center gap-1">
        <ProtectedVisibility requiredRoles={["ROLE_TEACHER"]} beta={true}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleLaunchQuizSelection(evaluation);
            }}
            className="p-2 text-gray-500 dark:text-gray-400 
 hover:text-gray-900 dark:hover:text-gray-200
rounded-full transition-colors
hover:bg-gray-100 dark:hover:bg-gray-700"
            title="Start Quiz"
            aria-label="Start Quiz"
          >
            <div className="relative inline-block">
              <span className="text-lg font-semibold">Quiz</span>
              <span
                className="absolute -top-2 -right-6 px-2 py-0.5 text-xs font-small tracking-wide font-bold
 text-gray-800 bg-gray-100 rounded-full dark:bg-gray-700 dark:text-gray-300
transform rotate-12 shadow-sm"
              >
                beta
              </span>
            </div>
          </button>
        </ProtectedVisibility>

        <button
          onClick={(e) => handleViewQuestions(evaluation, e)}
          className="p-2 text-gray-500 dark:text-gray-400 
 hover:text-gray-900 dark:hover:text-gray-200
 rounded-full transition-colors
 hover:bg-gray-100 dark:hover:bg-gray-700"
          title="View Questions"
          aria-label="View Questions"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
            />
          </svg>
        </button>

        <KebabMenu
          onAssignToBatch={!isArchived && !isExpired ? () => handleAssignToBatch(evaluation) : undefined}
          onEdit={hasZeroAssignments ? () => handleEditEvaluation(evaluation) : undefined}
          editLabel={
            isArchived
              ? (t("modifyAndReuse") || "Modify & Reuse")
              : isExpired
              ? (t("editAndReuse") || "Edit & Reuse")
              : (t("edit") || "Edit")
          }
          onArchive={!isArchived && hasZeroAssignments ? () => handleArchiveEvaluation(evaluation) : undefined}
          archiveLabel={t("archive") || "Archive"}
          onUnarchive={!isExpired && isArchived ? () => handleUnarchiveEvaluation(evaluation) : undefined}
          unarchiveLabel={t("unarchive") || "Restore to Active"}
          menuItems={
            !hasZeroAssignments && assignmentsLoaded
              ? [
                  {
                    label: isExpired
                      ? `🔒 ${assignmentCount} Assigned (${t("expiredInUse") || "Expired & In Use"})`
                      : `🔒 ${assignmentCount} Assigned (${t("inUseAssignments") || "In Use"})`,
                    onClick: () => {
                      if (typeof displayToast === "function") {
                        displayToast(
                          isExpired
                            ? (t("expiredInUseNotice") ||
                                "This evaluation has expired and is in use by student assignments and cannot be modified or archived.")
                            : (t("evaluationInUseNotice") ||
                                "This evaluation is in use by active student assignments and cannot be modified or archived."),
                          "info"
                        );
                      }
                    },
                  },
                ]
              : []
          }
        />
      </div>

      {/* Card Header & Content */}
      <div className="w-full">
        {/* Archived Pill */}
        {isArchived && (
          <span className="inline-flex items-center gap-1 text-xs px-2.5 py-0.5 rounded-full font-semibold bg-purple-100 text-purple-800 dark:bg-purple-900/60 dark:text-purple-300 border border-purple-200 dark:border-purple-800 mb-1.5 shadow-2xs">
            📦 {t("archived") || "Archived"}
          </span>
        )}

        {/* Title with padding right to prevent overlap with top action buttons */}
        <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 tracking-wide mb-1 pr-20">
          {evaluation.name}
        </h3>

        {/* Meta info */}
        <p className="text-sm text-gray-700 dark:text-gray-400">
          {evaluation.conceptTopic || "-"} • {evaluation.difficulty}
        </p>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {evaluation.numberOfQuestions} Qs • {evaluation.maxTimeMinutes} mins
        </p>
        <p className={`text-xs mt-2 italic ${isExpired ? "text-red-600 dark:text-red-400" : "text-gray-500 dark:text-gray-400"}`}>
          Due: {evaluation.expiryDateTime?.split("T")[0] || "-"}
        </p>
      </div>

      {/* Bottom Footer: Practice badge on left, Expired badge & Assignment count on bottom right */}
      <div className="w-full mt-4 pt-3 flex items-center justify-between gap-2 border-t border-gray-100 dark:border-gray-800/80">
        <div>
          {evaluation.isPractice && (
            <span
              className="text-xs px-2.5 py-1 
 bg-gray-200 text-gray-800 
 dark:bg-gray-700 dark:text-gray-200
 rounded-full font-medium tracking-wide"
            >
              PRACTICE
            </span>
          )}
        </div>

        {/* Expired Tag & Number of Assignments at Bottom Right */}
        <div className="ml-auto flex items-center gap-2">
          {isExpired && (
            <span
              className="inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300 border border-red-300 dark:border-red-700 shadow-xs"
              title={
                hasZeroAssignments
                  ? (t("expiredZeroAssignmentsTooltip") || "0 assignments — this expired evaluation is unused and can be edited or reused.")
                  : (t("expiredEvaluationCannotBeModified") || "Evaluation has expired.")
              }
            >
              <span>⏰</span>
              <span>{t("expired") || "Expired"}</span>
            </span>
          )}

          {assignmentsLoaded ? (
            hasZeroAssignments ? (
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-900 dark:bg-amber-950/70 dark:text-amber-200 border border-amber-300 dark:border-amber-700 shadow-xs"
                title={t("unusedEvaluationTooltip") || "0 assignments — this evaluation is unused and can be edited or deleted."}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span>
                <span>{t("zeroAssignments") || "0 Assignments"}</span>
              </span>
            ) : (
              <span
                className="inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-medium rounded-full bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 border border-blue-200 dark:border-blue-800/60"
                title={`This evaluation has ${assignmentCount} assignment(s) and is currently in use.`}
              >
                <svg className="w-3.5 h-3.5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>{assignmentCount} {assignmentCount === 1 ? "Assignment" : "Assignments"}</span>
              </span>
            )
          ) : (
            <span className="inline-flex items-center px-2 py-0.5 text-xs text-gray-400 bg-gray-100 dark:bg-gray-800 rounded-full animate-pulse">
              Checking assignments...
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const EvaluationsContent = ({ navigateTo, useMockData, displayToast, showQuestions }) => {
  const [courses, setCourses] = useState([]);
  const [activeEvaluations, setActiveEvaluations] = useState([]);
  const [archivedEvaluations, setArchivedEvaluations] = useState([]);
  const evaluations = useMemo(
    () => [...activeEvaluations, ...archivedEvaluations],
    [activeEvaluations, archivedEvaluations]
  );
  const [assignmentCountMap, setAssignmentCountMap] = useState({});
  const [assignmentsLoaded, setAssignmentsLoaded] = useState(false);
  const [filterUnusedOnly, setFilterUnusedOnly] = useState(false);
  const [filterArchivedOnly, setFilterArchivedOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // State for expanding sections
  const [expandedCourses, setExpandedCourses] = useState({});
  const [isRecentExpanded, setIsRecentExpanded] = useState(false);

  const [selectedEvaluation, setSelectedEvaluation] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);

  // NEW STATE: To control the Quiz Mode Selection Modal
  const [quizModeSelectionEvaluation, setQuizModeSelectionEvaluation] = useState(null);

  const { t } = useTranslation();
  const { startLoading, stopLoading } = useLoading();
  const { limits, canPerformAction } = useSubscription();
  const userId = getCurrentUserId();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Fetch Courses
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const url = `/courses?isActive.equals=true&createdBy.equals=${userId}&size=10000`;
        const data = await apiCall(
          url,
          useMockData,
          "GET",
          null,
          startLoading,
          stopLoading,
          displayToast
        );
        setCourses(data || []);
      } catch (error) {
        console.error("Failed to fetch courses:", error);
      }
    };

    if (userId) {
      fetchCourses();
    }
  }, [userId]);

  // Fetch Active Evaluations (Lightweight initial load with isActive.equals=true)
  const fetchActiveEvaluations = useCallback(async () => {
    if (!userId) return;
    try {
      const activeRes = await apiCall(
        `/evaluations?isActive.equals=true&createdBy.specified=true&createdBy.equals=${userId}&size=10000`,
        useMockData,
        "GET",
        null,
        startLoading,
        stopLoading,
        displayToast
      ).catch(async () => {
        return apiCall(
          `/evaluations?isActive.equals=true&createdBy.equals=${userId}&size=10000`,
          useMockData,
          "GET",
          null,
          startLoading,
          stopLoading,
          displayToast
        ).catch(async () => {
          return apiCall(
            `/evaluations?isActive.equals=true&size=10000`,
            useMockData,
            "GET",
            null,
            startLoading,
            stopLoading,
            displayToast
          );
        });
      });

      const activeList = Array.isArray(activeRes) ? activeRes : activeRes?.content ?? [];
      setActiveEvaluations(activeList);
    } catch (error) {
      console.error("Failed to fetch active evaluations:", error);
    }
  }, [userId, useMockData, startLoading, stopLoading, displayToast]);

  // Fetch Archived Evaluations on-demand (when user clicks "Show Archived" with isActive.equals=false)
  const fetchArchivedEvaluations = useCallback(async () => {
    if (!userId) return;
    try {
      const archivedRes = await apiCall(
        `/evaluations?isActive.equals=false&createdBy.specified=true&createdBy.equals=${userId}&size=10000`,
        useMockData,
        "GET",
        null,
        startLoading,
        stopLoading,
        displayToast
      ).catch(async () => {
        return apiCall(
          `/evaluations?isActive.equals=false&createdBy.equals=${userId}&size=10000`,
          useMockData,
          "GET",
          null,
          startLoading,
          stopLoading,
          displayToast
        ).catch(async () => {
          return apiCall(
            `/evaluations?isActive.equals=false&size=10000`,
            useMockData,
            "GET",
            null,
            startLoading,
            stopLoading,
            displayToast
          ).catch(() => []);
        });
      });

      const archivedList = Array.isArray(archivedRes) ? archivedRes : archivedRes?.content ?? [];
      setArchivedEvaluations(archivedList);
    } catch (error) {
      console.error("Failed to fetch archived evaluations:", error);
    }
  }, [userId, useMockData, startLoading, stopLoading, displayToast]);

  // Toggle showing archived evaluations (fetching on demand if opening)
  const handleToggleArchived = useCallback(async () => {
    if (!filterArchivedOnly) {
      await fetchArchivedEvaluations();
      setFilterArchivedOnly(true);
    } else {
      setFilterArchivedOnly(false);
    }
  }, [filterArchivedOnly, fetchArchivedEvaluations]);

  useEffect(() => {
    if (courses.length > 0 && userId) {
      fetchActiveEvaluations();
    }

    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [courses, userId, fetchActiveEvaluations]);

  // Fetch Assignments to identify evaluations with zero assignments
  useEffect(() => {
    const fetchAssignments = async () => {
      try {
        const data = await apiCall(
          "/assignments?size=10000",
          useMockData,
          "GET",
          null,
          null,
          null,
          displayToast
        );
        const list = Array.isArray(data) ? data : data?.content ?? [];
        const counts = {};
        list.forEach((a) => {
          const evalId = a.evaluation?.id ?? a.evaluationId ?? a.evaluationLink;
          if (evalId != null) {
            counts[evalId] = (counts[evalId] || 0) + 1;
          }
        });
        setAssignmentCountMap(counts);
        setAssignmentsLoaded(true);
      } catch (error) {
        console.error("Failed to fetch assignments:", error);
        setAssignmentsLoaded(true);
      }
    };

    fetchAssignments();
  }, [useMockData]);

  const getAssignmentCount = useCallback(
    (evaluation) => {
      if (typeof evaluation?.assignmentCount === "number") return evaluation.assignmentCount;
      if (Array.isArray(evaluation?.assignments)) return evaluation.assignments.length;
      return assignmentCountMap[evaluation?.id] || 0;
    },
    [assignmentCountMap]
  );

  const handleEditEvaluation = useCallback(
    (evaluation) => {
      const courseId = evaluation.course?.id ?? evaluation.courseId ?? "";
      navigateTo("updateEvaluation", evaluation.id, courseId);
    },
    [navigateTo]
  );

  const handleArchiveEvaluation = useCallback(
    async (evaluation) => {
      const confirmMsg =
        t("confirmArchiveEvaluation") ||
        `Are you sure you want to archive "${evaluation.name}"?`;
      if (!window.confirm(confirmMsg)) return;

      const courseId = evaluation.course?.id ?? evaluation.courseId ?? null;
      const cleanPutPayload = {
        id: evaluation.id,
        name: evaluation.name,
        chapter: evaluation.chapter,
        conceptTopic: evaluation.conceptTopic,
        difficulty: evaluation.difficulty,
        source: evaluation.source,
        numberOfQuestions: evaluation.numberOfQuestions,
        maxTimeMinutes: evaluation.maxTimeMinutes,
        expiryDateTime: evaluation.expiryDateTime,
        isPractice: evaluation.isPractice,
        isActive: false,
        course: courseId ? { id: courseId } : null,
      };

      try {
        try {
          // Prefer PATCH for partial updates without relational constraint errors
          await apiCall(
            `/evaluations/${evaluation.id}`,
            useMockData,
            "PATCH",
            { id: evaluation.id, isActive: false },
            startLoading,
            stopLoading,
            displayToast
          );
        } catch {
          // Fallback to clean PUT without unknown non-entity fields
          await apiCall(
            `/evaluations/${evaluation.id}`,
            useMockData,
            "PUT",
            cleanPutPayload,
            startLoading,
            stopLoading,
            displayToast
          );
        }

        if (typeof displayToast === "function") {
          displayToast(
            t("evaluationArchivedSuccessfully") || "Evaluation archived successfully",
            "success"
          );
        }

        const archivedItem = {
          ...evaluation,
          isActive: false,
          isArchived: true,
          status: "ARCHIVED",
        };

        setActiveEvaluations((prev) => prev.filter((e) => e.id !== evaluation.id));
        setArchivedEvaluations((prev) => [
          archivedItem,
          ...prev.filter((e) => e.id !== evaluation.id),
        ]);
      } catch (err) {
        console.error("Failed to archive evaluation:", err);
        if (typeof displayToast === "function") {
          displayToast(
            t("failedToArchiveEvaluation") || `Failed to archive evaluation: ${err.message}`,
            "error"
          );
        }
      }
    },
    [useMockData, startLoading, stopLoading, displayToast, t]
  );

  const handleUnarchiveEvaluation = useCallback(
    async (evaluation) => {
      const courseId = evaluation.course?.id ?? evaluation.courseId ?? null;
      const cleanPutPayload = {
        id: evaluation.id,
        name: evaluation.name,
        chapter: evaluation.chapter,
        conceptTopic: evaluation.conceptTopic,
        difficulty: evaluation.difficulty,
        source: evaluation.source,
        numberOfQuestions: evaluation.numberOfQuestions,
        maxTimeMinutes: evaluation.maxTimeMinutes,
        expiryDateTime: evaluation.expiryDateTime,
        isPractice: evaluation.isPractice,
        isActive: true,
        course: courseId ? { id: courseId } : null,
      };

      try {
        try {
          await apiCall(
            `/evaluations/${evaluation.id}`,
            useMockData,
            "PATCH",
            { id: evaluation.id, isActive: true },
            startLoading,
            stopLoading,
            displayToast
          );
        } catch {
          await apiCall(
            `/evaluations/${evaluation.id}`,
            useMockData,
            "PUT",
            cleanPutPayload,
            startLoading,
            stopLoading,
            displayToast
          );
        }

        if (typeof displayToast === "function") {
          displayToast(
            t("evaluationRestoredSuccessfully") || "Evaluation restored to active status",
            "success"
          );
        }

        const restoredItem = {
          ...evaluation,
          isActive: true,
          isArchived: false,
          status: "ACTIVE",
        };

        setArchivedEvaluations((prev) => prev.filter((e) => e.id !== evaluation.id));
        setActiveEvaluations((prev) => [
          restoredItem,
          ...prev.filter((e) => e.id !== evaluation.id),
        ]);
      } catch (err) {
        console.error("Failed to restore evaluation:", err);
        if (typeof displayToast === "function") {
          displayToast(
            t("failedToRestoreEvaluation") || `Failed to restore evaluation: ${err.message}`,
            "error"
          );
        }
      }
    },
    [useMockData, startLoading, stopLoading, displayToast, t]
  );

  // Helper to determine if an evaluation is archived / inactive / soft-deleted
  const isEvaluationArchived = useCallback((evaluation) => {
    if (!evaluation) return false;
    return (
      evaluation.isActive === false ||
      evaluation.isArchived === true ||
      evaluation.archived === true ||
      evaluation.status === "ARCHIVED" ||
      evaluation.status === "INACTIVE" ||
      evaluation.status === "DELETED"
    );
  }, []);

  const isEvaluationActive = useCallback(
    (evaluation) => !isEvaluationArchived(evaluation),
    [isEvaluationArchived]
  );

  const archivedCount = useMemo(() => {
    return archivedEvaluations.length;
  }, [archivedEvaluations]);

  const activeCount = useMemo(() => {
    return activeEvaluations.length;
  }, [activeEvaluations]);

  // Unused evaluations count (based on current active/archived view)
  const unusedCount = useMemo(() => {
    if (!assignmentsLoaded) return 0;
    const base = filterArchivedOnly ? archivedEvaluations : activeEvaluations;
    return base.filter((e) => getAssignmentCount(e) === 0).length;
  }, [archivedEvaluations, activeEvaluations, assignmentsLoaded, getAssignmentCount, filterArchivedOnly]);

  // Filtered evaluations by active/archived status, search query & unused toggle
  const filteredEvaluations = useMemo(() => {
    let result = filterArchivedOnly ? archivedEvaluations : activeEvaluations;

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (e) =>
          e.name?.toLowerCase().includes(q) ||
          e.conceptTopic?.toLowerCase().includes(q) ||
          e.chapter?.toLowerCase().includes(q)
      );
    }

    if (filterUnusedOnly && assignmentsLoaded) {
      result = result.filter((e) => getAssignmentCount(e) === 0);
    }

    return result;
  }, [
    activeEvaluations,
    archivedEvaluations,
    filterArchivedOnly,
    searchQuery,
    filterUnusedOnly,
    assignmentsLoaded,
    getAssignmentCount,
  ]);

  // --- Recent Evaluations ---
  const recentEvaluations = useMemo(() => {
    if (!filteredEvaluations || filteredEvaluations.length === 0) return [];

    // 1. Sort all evaluations by date (Newest First)
    const sorted = [...filteredEvaluations].sort((a, b) => {
      const dateA = a.createdDate ? new Date(a.createdDate) : new Date(0);
      const dateB = b.createdDate ? new Date(b.createdDate) : new Date(0);
      return dateB - dateA;
    });

    // 2. Filter for items strictly within the last 2 weeks
    const twoWeeksAgo = new Date();
    twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
    const lastTwoWeeksDocs = sorted.filter((e) => new Date(e.createdDate) >= twoWeeksAgo);

    // 3. Rule: If data exists in last 2 weeks, show it. Otherwise show last 3.
    if (lastTwoWeeksDocs.length > 0) {
      return lastTwoWeeksDocs;
    } else {
      return sorted.slice(0, 3);
    }
  }, [filteredEvaluations]);

  // Group logic for Course lists
  const groupedEvaluations = useMemo(() => {
    return groupAndSortEvaluations(filteredEvaluations);
  }, [filteredEvaluations]);

  const handleAssignToBatch = (evaluation) => {
    navigateTo("assignEvaluationToBatch", null, null, evaluation.id);
  };

  // NEW HANDLER: Opens the quiz mode selection modal
  const handleLaunchQuizSelection = (evaluation) => {
    setQuizModeSelectionEvaluation(evaluation);
  };

  // NEW HANDLER: Closes the quiz mode selection modal
  const handleCloseQuizSelection = () => {
    setQuizModeSelectionEvaluation(null);
  };

  // NEW HANDLER: Navigates after a mode is selected
  const handleStartQuiz = (evaluationId, mode) => {
    handleCloseQuizSelection(); // Close the modal
    // Navigate, passing the mode as a parameter (e.g., query param in a real app)
    // Assuming navigateTo signature is (route, id, params)
    navigateTo("startQuiz", evaluationId, mode);
  };


  const handleViewQuestions = useCallback(
    async (evaluation, e) => {
      e.stopPropagation(); // Prevent card click
      setSelectedEvaluation(evaluation);
      setLoadingQuestions(true);
      setQuestions([]);
      loadMathJaxScript();

      try {
        // Get question codes from evaluation
        let questionCodes =
          evaluation?.questionIds || evaluation?.questionCodes || [];
        if (typeof questionCodes === "string") {
          questionCodes = questionCodes
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
        }

        if (Array.isArray(questionCodes) && questionCodes.length > 0) {
          const res = await masterApiCall(
            "/questions/by-codes",
            useMockData,
            "POST",
            { questionCodes },
            startLoading,
            stopLoading,
            displayToast
          );
          const fetchedQuestions = Array.isArray(res)
            ? res
            : res?.content ?? [];
          setQuestions(fetchedQuestions);
        } else {
          if (typeof displayToast === "function") {
            displayToast("No questions found for this evaluation", "info");
          }
          setSelectedEvaluation(null);
        }
      } catch (err) {
        console.error("Failed to fetch questions:", err);
        if (typeof displayToast === "function") {
          displayToast("Failed to load questions", "error");
        }
        setSelectedEvaluation(null);
      } finally {
        setLoadingQuestions(false);
      }
    },
    [useMockData, startLoading, stopLoading, displayToast]
  );

  const handleCloseQuestionsModal = () => {
    setSelectedEvaluation(null);
    setQuestions([]);
  };

  const toggleCourseExpansion = (courseId) => {
    setExpandedCourses((prev) => ({
      ...prev,
      [courseId]: !prev[courseId],
    }));
  };

  const getVisibleCardCount = () => {
    if (windowWidth >= 1024) {
      return 3;
    } else if (windowWidth >= 640) {
      return 2;
    } else {
      return 1;
    }
  };

  const canCreateEvaluation = canPerformAction(
    "maxEvaluationsPerTutorLimit",
    activeCount
  );

  const rightSideContentTop = (
    <div className="flex items-center gap-3 flex-wrap">
      {/* Show Archived Evaluations Toggle Button */}
      <button
        onClick={handleToggleArchived}
        className={`px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-all shadow-xs ${
          filterArchivedOnly
            ? "bg-purple-700 text-white shadow-sm ring-2 ring-purple-400"
            : "bg-purple-50 text-purple-800 dark:bg-purple-950/60 dark:text-purple-300 border border-purple-300 dark:border-purple-700 hover:bg-purple-100 dark:hover:bg-purple-900/40"
        }`}
        title="Toggle between active and archived evaluations"
      >
        <span>📦</span>
        <span>
          {filterArchivedOnly
            ? `${t("hideArchived") || "Showing Archived"} (${archivedEvaluations.length})`
            : t("showArchived") || "Show Archived"}
        </span>
      </button>

      {/* 0 Assignments (Unused) Filter Toggle */}
      {assignmentsLoaded && (
        <button
          onClick={() => setFilterUnusedOnly((prev) => !prev)}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-all shadow-xs ${
            filterUnusedOnly
              ? "bg-amber-600 text-white shadow-sm ring-2 ring-amber-400"
              : "bg-amber-50 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border border-amber-300 dark:border-amber-700 hover:bg-amber-100 dark:hover:bg-amber-900/40"
          }`}
          title="Filter evaluations with 0 assignments (unused, can be modified or archived)"
        >
          <span
            className={`w-2 h-2 rounded-full ${
              filterUnusedOnly ? "bg-white" : "bg-amber-500 animate-pulse"
            }`}
          ></span>
          <span>
            {t("zeroAssignments") || "0 Assignments"} ({unusedCount})
          </span>
        </button>
      )}

      {/* Search Input */}
      <div className="relative w-full max-w-44">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={t("search")}
          className="w-full px-4 py-2 pl-9 pr-7 text-sm border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600 text-gray-900"
        />
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">
          🔍
        </span>
        {searchQuery && (
          <button
            onClick={() => setSearchQuery("")}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xs font-bold"
            title="Clear search"
          >
            ✕
          </button>
        )}
      </div>

      <button
        onClick={() => navigateTo("createEvaluation")}
        className={`px-5 py-2 rounded-lg font-medium transition-colors ${canCreateEvaluation
          ? "bg-gray-800 text-gray-100 hover:bg-gray-700 dark:bg-gray-600 dark:hover:bg-gray-500"
          : "bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-400"
          }`}
        disabled={!canCreateEvaluation}
      >
        {t("createEvaluation")}
      </button>
    </div>
  );

  const isFiltered = filterArchivedOnly || filterUnusedOnly || Boolean(searchQuery.trim());

  // Calculate Recent Items to Show based on Toggle
  const visibleCardCount = getVisibleCardCount();
  const recentToShow = isRecentExpanded
    ? recentEvaluations
    : recentEvaluations.slice(0, visibleCardCount);
  const hasMoreRecent = recentEvaluations.length > visibleCardCount;

  return (
    <div className="p-8 md:px-10 ">
      <ContentHeaderSection
        title={t("evaluations")}
        subtitle={t("manageReviewEvaluations")}
        rightContentTop={rightSideContentTop}
        rightContentBottom={
          <FeatureLimitDisplay
            feature="maxEvaluationsPerTutor"
            currentCount={activeCount}
            limit={limits.maxEvaluationsPerTutor}
          />
        }
      />

      {/* --- RECENT EVALUATIONS SECTION (Hidden when viewing filtered content: archived, unused, or search) --- */}
      {!isFiltered && recentEvaluations.length > 0 && (
        <div className="mb-10 border-b-2 border-dashed border-gray-200 dark:border-gray-700 pb-2">
          <div className="flex justify-between items-center pb-4">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <span className="text-2xl">🕒</span>{" "}
              {t("recentEvaluations") || "Recent Evaluations"}
            </h2>

            {/* View All / View Less Toggle for Recent */}
            {hasMoreRecent && (
              <div
                className="relative group cursor-pointer"
                onClick={() => setIsRecentExpanded(!isRecentExpanded)}
              >
                <span className="relative z-10 text-sm font-medium text-blue-600 transition-all duration-300 group-hover:text-blue-800 dark:text-blue-400 dark:group-hover:text-blue-200">
                  {isRecentExpanded
                    ? `View Less (${recentEvaluations.length})`
                    : `View All (${recentEvaluations.length - visibleCardCount})`}
                </span>
                {/* Stacked cards effect */}
                <span className="absolute inset-0 z-0 bg-blue-100 rounded-sm -top-1 left-2 w-10 h-6 transform rotate-3 origin-bottom-right transition-all duration-300 group-hover:rotate-6 group-hover:translate-x-1 dark:bg-blue-900"></span>
                <span className="absolute inset-0 z-0 bg-blue-200 rounded-sm top-0 left-1 w-10 h-6 transform -rotate-3 origin-top-left transition-all duration-300 group-hover:rotate-0 group-hover:-translate-x-1 dark:bg-blue-800"></span>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {recentToShow.map((evaluation) => (
              <EvaluationCard
                key={`recent-${evaluation.id}`}
                evaluation={evaluation}
                assignmentCount={getAssignmentCount(evaluation)}
                assignmentsLoaded={assignmentsLoaded}
                navigateTo={navigateTo}
                handleViewQuestions={handleViewQuestions}
                handleAssignToBatch={handleAssignToBatch}
                handleLaunchQuizSelection={handleLaunchQuizSelection}
                handleEditEvaluation={handleEditEvaluation}
                handleArchiveEvaluation={handleArchiveEvaluation}
                handleUnarchiveEvaluation={handleUnarchiveEvaluation}
                t={t}
                displayToast={displayToast}
              />
            ))}
          </div>
        </div>
      )}
      {/* --- EXISTING COURSE LISTS --- */}
      {Object.keys(groupedEvaluations).length === 0 ? (
        <div className="col-span-full text-center text-gray-600 dark:text-gray-400 mt-10">
          {filterArchivedOnly
            ? "No archived evaluations found."
            : filterUnusedOnly
            ? "No unused evaluations found."
            : t("noEvaluationsFound")}
        </div>
      ) : (
        <div className="space-y-12">
          {Object.entries(groupedEvaluations).map(
            ([courseId, courseEvaluations]) => {
              const isExpanded = expandedCourses[courseId];
              const evaluationsToShow = isExpanded
                ? courseEvaluations
                : courseEvaluations.slice(0, visibleCardCount);
              const hasMore = courseEvaluations.length > visibleCardCount;
              const courseName = courses.find(
                (c) => c.id == courseId
              )?.courseName;

              return (
                <div key={courseId} className="space-y-6">
                  <div className="flex justify-between items-center pb-2 border-b-2 border-gray-200 dark:border-gray-700">
                    <h2 className="flex items-center text-2xl font-bold text-gray-900 dark:text-gray-100">
                      <span className="px-3 py-1 mr-3 text-sm font-medium tracking-wide text-gray-800 bg-gray-200 rounded-full dark:bg-gray-700 dark:text-gray-300">
                        Course
                      </span>
                      {courseName}
                    </h2>
                    {hasMore && (
                      <div
                        className="relative group cursor-pointer"
                        onClick={() => toggleCourseExpansion(courseId)}
                      >
                        {/* Top card */}
                        <span className="relative z-10 text-sm font-medium text-blue-600 transition-all duration-300 group-hover:text-blue-800 dark:text-blue-400 dark:group-hover:text-blue-200">
                          {isExpanded
                            ? `View Less (${courseEvaluations.length})`
                            : `View All (${courseEvaluations.length - visibleCardCount
                            })`}
                        </span>
                        {/* Stacked cards visualization */}
                        <span className="absolute inset-0 z-0 bg-blue-100 rounded-sm -top-1 left-2 w-10 h-6 transform rotate-3 origin-bottom-right transition-all duration-300 group-hover:rotate-6 group-hover:translate-x-1 dark:bg-blue-900"></span>
                        <span className="absolute inset-0 z-0 bg-blue-200 rounded-sm top-0 left-1 w-10 h-6 transform -rotate-3 origin-top-left transition-all duration-300 group-hover:rotate-0 group-hover:-translate-x-1 dark:bg-blue-800"></span>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
                    {evaluationsToShow.map((evaluation) => (
                      <EvaluationCard
                        key={evaluation.id}
                        evaluation={evaluation}
                        assignmentCount={getAssignmentCount(evaluation)}
                        assignmentsLoaded={assignmentsLoaded}
                        navigateTo={navigateTo}
                        handleViewQuestions={handleViewQuestions}
                        handleAssignToBatch={handleAssignToBatch}
                        handleLaunchQuizSelection={handleLaunchQuizSelection}
                        handleEditEvaluation={handleEditEvaluation}
                        handleArchiveEvaluation={handleArchiveEvaluation}
                        handleUnarchiveEvaluation={handleUnarchiveEvaluation}
                        t={t}
                        displayToast={displayToast}
                      />
                    ))}
                  </div>
                </div>
              );
            }
          )}
        </div>
      )}

      {/* Questions View Modal */}
      {selectedEvaluation && (
        ShowQuestions(selectedEvaluation, questions, handleCloseQuestionsModal, loadingQuestions, t)
      )}

      {/* NEW: Quiz Mode Selection Modal */}
      {quizModeSelectionEvaluation && (
        <QuizModeSelectionModal
          evaluation={quizModeSelectionEvaluation}
          onClose={handleCloseQuizSelection}
          onSelectMode={handleStartQuiz}
          t={t}
        />
      )}

    </div>
  );
};

export default EvaluationsContent;

